// services/userService.ts
import { apiService } from './api';

export interface User {
  users_id: number;
  users_name: string;
  email: string;
  phone?: string;
  designation: string;
  role: {
    id: number;
    role_name: string;
  };
  role_id: number;
  is_active: boolean;
  is_superuser?: boolean;
  last_login?: string;
  created_at: string;
  created_by: string;
  updated_at?: string;
  updated_by?: string;
}

export interface Role {
  id: number;
  role_name: string;
}

export interface CreateUserRequest {
  users_name: string;
  email: string;
  phone?: string;
  designation: string;
  password: string;
  confirmPassword: string;
  role_id: number;
  is_active?: boolean;
  is_superuser?: boolean;
}

export interface UpdateUserRequest {
  users_name?: string;
  role_id?: number;
  designation: string;
  is_active?: boolean;
  is_superuser?: boolean;
  password?: string;
}

export interface UserListParams {
  page?: number;
  limit?: number;
  search?: string;
  role_id?: number;
  is_active?: boolean;
}

export interface UserListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: User[];
}

class UserService {
  /**
   * Get all users (paginated)
   */
  async getUsers(params?: UserListParams): Promise<UserListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role_id) queryParams.append('role_id', params.role_id.toString());
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());

    const queryString = queryParams.toString();
    const url = `/users/${queryString ? `?${queryString}` : ''}`;
    
    return apiService.get<UserListResponse>(url);
  }

  /**
   * Get single user by ID
   */
  async getUserById(userId: number): Promise<User> {
    return apiService.get<User>(`/users/${userId}/`);
  }

  /**
   * Create new user
   */
  async createUser(data: CreateUserRequest): Promise<User> {
    return apiService.post<User>('/users/', data);
  }

  /**
   * Update existing user
   */
  async updateUser(userId: number, data: UpdateUserRequest): Promise<User> {
    return apiService.patch<User>(`/users/${userId}/`, data);
  }

  /**
   * Delete user
   */
  async deleteUser(userId: number): Promise<void> {
    return apiService.delete(`/users/${userId}/`);
  }

  /**
   * Get all available roles
   */
  async getRoles(): Promise<Role[]> {
    const response = await apiService.get<any>('/roles/');
    
    // Handle both array response and paginated response from DRF ViewSet
    if (Array.isArray(response)) {
      return response;
    } else if (response && Array.isArray(response.results)) {
      return response.results;
    }
    
    // If it's neither, return empty array
    console.error('Unexpected roles response format:', response);
    return [];
  }

  /**
   * Toggle user active status
   */
  async toggleUserStatus(userId: number, isActive: boolean): Promise<User> {
    return apiService.patch<User>(`/users/${userId}/`, { is_active: isActive });
  }

  /**
   * Reset user password
   */
  async resetUserPassword(userId: number, newPassword: string): Promise<{ success: boolean; message: string }> {
    return apiService.post(`/users/${userId}/reset-password/`, { password: newPassword });
  }

  /**
   * Bulk update users
   */
  async bulkUpdateUsers(userIds: number[], data: UpdateUserRequest): Promise<{ success: boolean; message: string }> {
    return apiService.post('/users/bulk-update/', { user_ids: userIds, data });
  }
}

export const userService = new UserService();